import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import axios from "axios";
import { API_BASE_URL } from "../../Config";
import Swal from "sweetalert2";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material UI components
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Card from "@mui/material/Card";
import { useTheme } from "@mui/material";

function Complaints() {
  const showSidebar = useSelector((state) => state.sidebar.showSidebar);
  const [name, setName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [isValidContact, setIsValidContact] = useState(true);

  const handleNameChange = (e) => setName(e.target.value);

  const cid = sessionStorage.getItem("cid");
  const custName = sessionStorage.getItem("custName");
  const custType = sessionStorage.getItem("custType");
  const Accode = sessionStorage.getItem("accode");
  const emailid = sessionStorage.getItem("emailid");

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const handleContactNumberChange = (e) => {
    const value = e.target.value;
    setContactNumber(value);
    const isValid = /^\d{10}$/.test(value);
    setIsValidContact(isValid);
  };

  const handleSubjectChange = (e) => setSubject(e.target.value);
  const handleDescriptionChange = (e) => setDescription(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(`${API_BASE_URL}/complaint/save`, {
        cid: cid,
        assignedby: name,
        subject: subject,
        description: description,
        complainant: custName,
        compmobile: contactNumber,
        compemail: emailid,
        accode: Accode,
      });

      if (response.status === 200) {
        // Log the success
        console.log("Complaint Submitted:", { name, contactNumber, subject, description });

        // Show SweetAlert2 success message
        Swal.fire({
          icon: "success",
          title: "Complaint Raised!",
          text: `Success! Your complaint "${subject}" has been raised.`,
          timer: 1500,
          showConfirmButton: false,
        });

        // Clear the form
        clearForm();
      } else {
        // Handle unexpected response statuses
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong, please try again.",
          timer: 930,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      // Handle error case
      console.error("Error submitting complaint:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to raise complaint. Please try again.",
        timer: 930,
        showConfirmButton: false,
      });
    }
  };

  const clearForm = () => {
    setName("");
    setContactNumber("");
    setSubject("");
    setDescription("");
    setIsValidContact(true);
  };

  const isFormValid = () => {
    return name && contactNumber && subject && description && isValidContact;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        className={`mt-[24%] sm:mt-10 md:mt-0 lg:mt-0 2xl:mt-0 h-[80%] ${
          showSidebar
            ? "transition-all transition-slowest ease ml-20 lg:ml-[18%]"
            : "transition-all transition-slowest ease"
        }`}
      >
        <Card sx={{ p: 2, boxShadow: 3, marginTop: 3 }}>
          <MDBox component="form" onSubmit={handleSubmit} borderRadius="lg">
            <MDBox display="flex" flexWrap="wrap" mb={3}>
              <MDBox width={{ xs: "100%", sm: "50%" }} pr={{ sm: 2 }} mb={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text">
                  Name <span style={{ color: "red" }}>*</span>
                </MDTypography>
                <MDInput
                  type="text"
                  value={name}
                  onChange={handleNameChange}
                  placeholder="Enter your name"
                  fullWidth
                  required
                />
              </MDBox>
              <MDBox width={{ xs: "100%", sm: "50%" }} pl={{ sm: 2 }} mb={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text">
                  Contact Number <span style={{ color: "red" }}>*</span>
                </MDTypography>
                <MDInput
                  type="tel"
                  value={contactNumber}
                  onChange={handleContactNumberChange}
                  placeholder="Enter contact (10 digits)"
                  fullWidth
                  required
                  error={!isValidContact}
                  helperText={!isValidContact && "Please enter a valid 10-digit contact number."}
                />
              </MDBox>
            </MDBox>
            <MDBox mb={3}>
              <MDTypography variant="caption" fontWeight="medium" color="text">
                Subject <span style={{ color: "red" }}>*</span>
              </MDTypography>
              <FormControl fullWidth required sx={{ height: 56, mb: 2 }}>
                <Select
                  displayEmpty
                  value={subject}
                  onChange={handleSubjectChange}
                  renderValue={(selected) => (selected ? selected : <span>Select a subject</span>)}
                  sx={{ height: 56 }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300, // Limit dropdown height
                      },
                    },
                  }}
                >
                  <MenuItem value="" disabled sx={{ minHeight: 48 }}>
                    <span>Select a subject</span>
                  </MenuItem>
                  <MenuItem value="Complaint" sx={{ minHeight: 48 }}>
                    Complaint
                  </MenuItem>
                  <MenuItem value="Feedback" sx={{ minHeight: 48 }}>
                    Feedback
                  </MenuItem>
                  <MenuItem value="Query" sx={{ minHeight: 48 }}>
                    Query
                  </MenuItem>
                  <MenuItem value="Other" sx={{ minHeight: 48 }}>
                    Other
                  </MenuItem>
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mb={3}>
              <MDTypography variant="caption" fontWeight="medium" color="text">
                Description <span style={{ color: "red" }}>*</span>
              </MDTypography>
              <MDInput
                multiline
                rows={4}
                value={description}
                onChange={handleDescriptionChange}
                placeholder={`Describe your ${subject !== "" ? subject : "concern"}...`}
                fullWidth
                required
              />
            </MDBox>
            <MDButton type="submit" variant="gradient" color="info" disabled={!isFormValid()}>
              Send Complaint
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default Complaints;
